<template>
  <div class="row location-search-block__search floating-label" :class="{ 'has-focus is-floating': focused || hasValue }">
    <div class="col-12">
      <span class="pill large">
        <label class="location-search-block__label" :for="fieldId">{{ placeholdertext }}</label>
        <input 
          :id="fieldId"
          ref="place" 
          type="search" 
          :select-first-on-enter="true" 
          @keyup.enter="onEnter"
          @keydown.down="onArrowDown($event)"
          @keydown.up="onArrowUp($event)"
          @keydown.esc="closeDropdown"
          @input="onInput"
          v-model="searchTerm"
          aria-label="Search" 
          placeholder="" 
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="showPredictions ? 'true' : 'false'"
          aria-controls="location-block-autocomplete-listbox"
          aria-describedby="search-by-location-autocomplete"
          :aria-activedescendant="highlightedPrediction >= 0 ? `location-block-option-${highlightedPrediction}` : null"
          @blur="onBlur($event)" 
          @focus="onFocus($event)" />
        
        <ul v-if="showPredictions && predictions && predictions.length" 
            class="autocomplete-dropdown"
            role="listbox"
            id="location-block-autocomplete-listbox">
          <li v-for="(prediction, idx) in predictions"
              :key="prediction.placePrediction.id || idx"
              :id="`location-block-option-${idx}`"
              role="option"
              :aria-selected="idx === highlightedPrediction ? 'true' : 'false'"
              @mousedown.prevent="selectPrediction(idx)"
              :class="{ 'autocomplete-dropdown__option': true, 'highlighted': idx === highlightedPrediction }"
          >
            {{ prediction.placePrediction.text.text }}
          </li>
        </ul>
        
        <button v-on:click="setPlaceBySubmit" class="location-search-block__filters-search"><img
            src="/assets/images/search_icon_red.svg" alt="search" /></button></span>
      <p class="sr-only" id="search-by-location-autocomplete">Begin typing to search, the field will be completed as you type. Use arrow keys to navigate, Enter to select.</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { Loader } from '@googlemaps/js-api-loader';
import { googleApi, mapConfigs } from "../../../config/map";

export default {
  data() {
    return {
      advisorData: null,
      errored: false,
      noadvisors: false,
      locationSearch: null,
      index: 0,
      focused: false,
      errored: false,
      loading: false,
      showDefault: false,
      loaded: false,
      radius: 20,
      hideform: false,
      selectedPlace: null,
      searchTerm: '',
      fieldId: `locationIdField${Math.floor(Math.random() * 1000)}`,
      showPredictions: false,
      highlightedPrediction: -1,
      sessionToken: null,
    }
  },
  mounted() {
    var self = this;
    this.getSearchFromLocalStore(self);
    this.ensureGoogleMapsLoaded();
  },
  computed: {
    isMobile() {
      return this.$mq != 'desktop';
    },
    hasValue() {
      return this.searchTerm && this.searchTerm.length > 0;
    }
  },
  beforeUnmount() {
    if (this.sessionToken) {
      this.sessionToken = null;
    }
  },
  methods: {
    ...mapActions([
      "geolocateUser"
    ]),
    
    async ensureGoogleMapsLoaded() {
      // If API is already loaded, return resolved promise
      if (typeof google !== 'undefined' && google.maps) {
        return Promise.resolve();
      }

      // If loader is already in progress, return existing promise
      if (window.googleMapsApiLoader) {
        return window.googleMapsApiLoader;
      }

      // Create new loader
      const loader = new Loader({
        apiKey: googleApi.mapKey || process.env.GOOGLE_API_KEY,
        version: "weekly",
        libraries: ["places"]
      });

      window.googleMapsApiLoader = loader.load().then(() => {
        return true;
      }).catch((error) => {
        console.error('Error loading Google Maps API:', error);
        throw error;
      });

      return window.googleMapsApiLoader;
    },

    async onInput() {
      this.showPredictions = false;
      this.highlightedPrediction = -1;
      
      if (!this.searchTerm || !window.google || !google.maps || !google.maps.places || !google.maps.places.AutocompleteSuggestion) {
        this.predictions = null;
        return;
      }
      
      if (!this.sessionToken) {
        this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      }
      
      const locationRestriction = {
        west: mapConfigs.AutoCompleteBoundaries.west,
        north: mapConfigs.AutoCompleteBoundaries.north,
        east: mapConfigs.AutoCompleteBoundaries.east,
        south: mapConfigs.AutoCompleteBoundaries.south
      };
      
      const request = {
        input: this.searchTerm,
        sessionToken: this.sessionToken,
        locationRestriction,
        includedRegionCodes: mapConfigs.includedRegionCodes
      };
      
      try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        // Filter out invalid predictions
        this.predictions = suggestions.filter(prediction => 
          prediction && 
          prediction.placePrediction && 
          prediction.placePrediction.text && 
          prediction.placePrediction.text.text
        );
        this.showPredictions = this.predictions.length > 0;
      } catch (err) {
        console.error('Error fetching predictions:', err);
        this.predictions = null;
        this.showPredictions = false;
      }
    },
    
    onArrowDown() {
      if (!this.showPredictions || !this.predictions.length) return;
      if (typeof this.highlightedPrediction !== 'number' || this.highlightedPrediction < 0 || this.highlightedPrediction >= this.predictions.length) {
        this.highlightedPrediction = 0;
      } else if (this.highlightedPrediction < this.predictions.length - 1) {
        this.highlightedPrediction++;
      } else {
        this.highlightedPrediction = 0;
      }
    },
    
    onArrowUp() {
      if (!this.showPredictions || !this.predictions.length) return;
      if (typeof this.highlightedPrediction !== 'number' || this.highlightedPrediction < 0 || this.highlightedPrediction >= this.predictions.length) {
        this.highlightedPrediction = this.predictions.length - 1;
      } else if (this.highlightedPrediction > 0) {
        this.highlightedPrediction--;
      } else {
        this.highlightedPrediction = this.predictions.length - 1;
      }
    },
    
    onEnter() {
      if (this.showPredictions && this.highlightedPrediction >= 0) {
        this.selectPrediction(this.highlightedPrediction);
      } else {
        this.handlePlaceChange();
      }
    },
    
    onFocus(event) {
      this.focused = true;
      this.toggleLabel(event);
    },
    
    onBlur(event) {
      this.showPredictions = false;
      this.highlightedPrediction = -1;
      this.toggleLabel(event);
    },
    
    closeDropdown() {
      this.showPredictions = false;
      this.highlightedPrediction = -1;
    },
    
    async selectPrediction(idx) {
      const prediction = this.predictions[idx];
      if (!prediction) return;
      
      this.showPredictions = false;
      this.searchTerm = prediction.placePrediction.text.text;
      
      const place = await prediction.placePrediction.toPlace();
      try {
        await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });
      } catch (e) {
        // fallback: do nothing, location may still be present
      }
      
      this.selectedPlace = {
        formatted_address: place.formattedAddress,
        geometry: {
          location: place.location
        }
      };
      
      this.sessionToken = null; // Reset session token after selection
      
      this.setPlace(this.selectedPlace);
    },
    handlePlaceChange() {
      // Handle manual entry when user presses enter
      if (this.selectedPlace) {
        this.setPlace(this.selectedPlace);
      } else if (this.searchTerm) {
        // If no place selected from autocomplete, try to geocode the input
        this.geocodeAndSearch(this.searchTerm);
      }
    },
    
    async geocodeAndSearch(address) {
      try {
        await this.ensureGoogleMapsLoaded();
        const { Geocoder } = await google.maps.importLibrary("geocoding");
        const geocoder = new Geocoder();
        
        geocoder.geocode({ 
          address: address,
          componentRestrictions: { country: 'US' }
        }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const place = {
              formatted_address: results[0].formatted_address,
              geometry: results[0].geometry
            };
            this.setPlace(place);
          } else {
            console.error('Geocoding failed:', status);
          }
        });
      } catch (error) {
        console.error('Error geocoding address:', error);
      }
    },
    setPlace(place) {
      // Convert address to lat and lng and save.
      if (place && place.formatted_address) {
        this.searchTerm = place.formatted_address;
      }
      
      if (place && place.geometry) {
        let lat, lng;
        if (typeof place.geometry.location.lat === 'function') {
          lat = place.geometry.location.lat();
          lng = place.geometry.location.lng();
        } else {
          lat = place.geometry.location.lat;
          lng = place.geometry.location.lng;
        }
        
        const payload = {
          lat: lat,
          lng: lng,
          radius: "20",
          term: this.searchTerm
        }
        window.localStorage.setItem('userSearch', true);
        window.localStorage.setItem("userPayload", JSON.stringify(payload));
        window.location = "/locations"
        //EventBus.scrollToTop();
      }
    },
    setPlaceBySubmit() {
      const el = this.$refs.place;
      if(this.searchTerm && this.searchTerm.trim() !== '') {
        // Try to use selected place first, otherwise geocode the input
        if (this.selectedPlace) {
          this.setPlace(this.selectedPlace);
        } else {
          this.geocodeAndSearch(this.searchTerm);
        }
      }
    },
    getSearchFromLocalStore(self) {
      var isUserSearch = window.localStorage.getItem('userSearch');
      if (isUserSearch === null || isUserSearch === 'false') {
        return;
      }
      var payloadString = window.localStorage.getItem("userPayload");
      if (payloadString !== null) {
        var payload = JSON.parse(payloadString);
        if (payload.term !== undefined) {
          // Wait for the element to be available
          this.$nextTick(() => {
            if (self.$refs.place) {
              self.searchTerm = payload.term;
              self.$refs.place.value = payload.term;
              const searchEl = document.getElementsByClassName("location-search-block__search");
              if (searchEl && searchEl[0]) {
                searchEl[0].classList.add('is-floating');
              }
            }
          });
        }
      }
    },
    toggleLabel(event) {
      const parent = document.querySelector('.location-search-block__search')
      if (event.type === 'focus') {
        this.focused = true;
        // For IE support, keep the following add() methods separate
        if (parent) {
          parent.classList.add('has-focus')
          parent.classList.add('is-floating')
        }
      }
      if (event.type === 'blur') {
        this.focused = false;
        if (parent) {
          parent.classList.remove('has-focus')
          if (event.target.value === '') {
            parent.classList.remove('is-floating')
            //reset to user's location
            this.geolocateUser();
          }
        }
      }
    },
    async allowLocation() {
      if (navigator.userAgent.indexOf("iPhone") != -1) {
        navigator.geolocation.getCurrentPosition(
          position => {
            // if it is iPhone and user hits accept I use the Browser lat and long
            this.getAdvisors(position.coords.latitude, position.coords.longitude, this.advisortypecategory)
          },
          error => {
            console.error(error.message)
            // if not, I use the existing lat and long
            this.getAdvisors(this.latitude, this.longitude, this.advisortypecategory)
          }
        )
      } else {
        let advisorsBlockDefault = document.getElementById('featured-advisors')
        advisorsBlockDefault.classList.add('default-advisors-active')
        this.showDefault = true
      }
    },
    getPersonalizationData() {
      var self = this;
      var cached_userZip = window.localStorage.getItem('userZip');
      if (cached_userZip) {
        this.zipdata = cached_userZip;
        this.longdata = window.localStorage.getItem('longData');
        this.latdata = window.localStorage.getItem('latData');
        this.getInitialAdvisors()
      }
      else {
        if (window.localStorage.getItem('stopPersonalization') != "true") {
          var ipaddressTest = HERO_getQueryStringParam("ipaddress");
          var passData = "";
          if (HERO_getQueryStringParam("ipaddress") != "") {
            passData = "?ipaddress=" + ipaddressTest
          }
          $.ajax({
            type: 'GET',
            url: '/api/personalization' + passData,
            contentType: 'application/json; charset=utf-8',
            success: function (data) {
              if (data.geoLocationState == null && data.ipAddress == null &&
                data.geoLocationPostalZip == null && data.geoLongitude == null && data.geoLatitude == null) {
                window.localStorage.setItem('stopPersonalization', "true");
              }
              else if (data.geoLocationPostalZip != null && data.geoLongitude != null && data.geoLatitude != null) {
                if (data.geoLocationPostalZip != null) {
                  self.zipdata = data.geoLocationPostalZip;
                  window.localStorage.setItem('userZip', data.geoLocationPostalZip);
                }
                if (data.geoLongitude != null) {
                  self.longdata = data.geoLongitude;
                  window.localStorage.setItem('longData', data.geoLongitude);
                }
                if (data.geoLatitude != null) {
                  self.latdata = data.geoLatitude;
                  window.localStorage.setItem('latData', data.geoLatitude);
                }
                if (data.geoLocationState != null) {
                  window.localStorage.setItem('geoState', data.geoLocationState);
                }
                if (data.ipAddress != null) {
                  window.localStorage.setItem('ipAddress', data.ipAddress);
                }
                setTimeout(function () {
                  self.getInitialAdvisors()
                }, 400);
              }
              else {
                self.getInitialAdvisors()
              }
            },
            error: function () {
              self.getInitialAdvisors()
            }
          })
        }
      }
    }
  },
  props: {
    placeholdertext: String
  },
  watch: {
  }
}
</script>
